import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Events, NavController, ViewController } from 'ionic-angular';


import { RodeoService } from '../../../services/rodeo.service';
import { Rodeo } from '../../../model/rodeo';
import { RodeoEventRegistration } from '../../../model/rodeoEventRegistration';
import { RodeoEvent } from '../../../model/rodeoEvent';
import { RodeoEventRun } from '../../../model/rodeoEventRun';
import { Account } from '../../../model/account';
import { Subscription } from 'rxjs';
import { UserManager } from '../../../services/managers/user.manager';

import { RodeoEventSchedule } from '../../../model/rodeoEventSchedule';
import { Organization } from '../../../model/organization';
import { Arena } from '../../../model/arena';
import { EntryService } from '../../../services/entry.service';
import { RoundStyle } from '../../../model/roundStyle';
import { AccountHelper } from '../../../lib/accountHelper';
import { VideoUploadSection } from './videos/video-upload';
import { PhotoUploadSection } from './photos/photo-upload';
import { RodeoListPage } from './rodeo-list';
import { Secretary } from '../../../model/secretary';
import { RunService } from '../../../services/run.service';
import { RodeoEventService } from '../../../services/rodeoEvent.service';
import { ReportsSection } from './reports/reports';
import { PayoutConfig } from '../../../model/payoutConfig';
import { PayoutSource } from '../../../model/payout-source';
import { ResultsSection } from './results/results';
import { PrintService } from '../../../services/print-service';
import { ScoringSection } from './scoring/scoring';
import { WinnerService } from '../../../services/winner.service';
import { Sidepot } from '../../../model/sidepot';
import { ProgressionsService } from '../../../services/progressions.service';
import { SearchOptions } from '../../../model/searchOptions';
import { ChangeRequestStatus } from '../../../model/registration-change-request';
import { RequestService } from '../../../services/request.service';
import { FarmtekReader } from '../../../lib/farmtek-reader';
import { OrganizationService } from '../../../services/organization.service';
import { DateHelperService } from '../../../services/date-helper';

@Component({
    selector: 'manage-rodeo',
    templateUrl: 'rodeo-manage.html'
})
export class RodeoManagePage implements OnInit {
    @ViewChild('rodeoList') rodeoList: RodeoListPage;
    @ViewChild('scoring') scoring: ScoringSection;
    @ViewChild('results') results: ResultsSection;
    @ViewChild('videos') videos: VideoUploadSection;
    @ViewChild('photos') photos: PhotoUploadSection;
    @ViewChild('reports') reports: ReportsSection;

    @Input('rodeo') rodeo: Rodeo;

    viewMode = 'search';
    viewSubMode = 'overview';

    accountHelper = AccountHelper.instance();

    account: Account = new Account();

    sanctionedOrganizations: Organization[] = [];
    organizations: Organization[] = [];
    secretaries: Secretary[] = [];
    arenas: Arena[] = [];
    roundStyles: RoundStyle[] = [];
    availableEventTypes: any[];
    progressionOrderFormats: any[];

    loadingData: boolean = true;
    showReportsVisible: boolean = false;

    popoverActive: boolean;

    prompted: boolean;
    subscriptions: Subscription[] = [];
    printing = false;

    pendingRequestCount: number = 0;

    rodeoOrgStaff: Account[] = [];

    constructor(
        public nav: NavController,
        private rodeoService: RodeoService,
        private userManager: UserManager,
        private rodeoEventService: RodeoEventService,
        private entryService: EntryService,
        private runService: RunService,
        private viewCtrl: ViewController,
        private events: Events,
        private printService: PrintService,
        private winnerService: WinnerService,
        private progressionService: ProgressionsService,
        private requestService: RequestService,
        private farmtekReader: FarmtekReader,
        private orgService: OrganizationService,
        private dateHelper: DateHelperService) { }

    ngOnInit(): void {
        this.printService.printing.subscribe(printing => this.printing = printing);

        this.rodeoService.selectedRodeo.subscribe(rodeo => {
            if (rodeo) {
                this.rodeoSelected(rodeo);
            }
        });
    }
    load() {
        this.events.subscribe("RodeoDrawn", () => { this.rodeo.drawn = true });
        this.subscriptions.push(this.userManager.account.subscribe(account => {
            this.account = account as Account;
        }));

        this.showSearch();

        if (this.rodeo) {
            if (this.accountHelper.isStaffAdminOfEvent(this.rodeo)) {
                setTimeout(() => {
                    this.showEntries()
                }, 150);
            } else if (this.accountHelper.isStaffBasicSecretaryOfEvent(this.rodeo)) {
                setTimeout(() => {
                    this.showReports()
                }, 150);
            } else if (this.accountHelper.isStaffOfEvent(this.rodeo)) {
                setTimeout(() => {
                    this.showPhotos()
                }, 150);
            }
        }

        this.rodeoService.getRodeoSettingOptions()
            .then(data => {
                this.sanctionedOrganizations = (data as any).organizations;
                this.organizations = (data as any).organizations.filter(x => AccountHelper.instance().isStaffOfOrganization(x));
                this.arenas = (data as any).arenas;
                this.roundStyles = (data as any).roundStyles;
            });

        this.rodeoEventService.getEventTypes()
            .then(data => {
                this.availableEventTypes = data as string[];
            });

        this.progressionService.getProgressionOrderFormats()
            .then(data => {
                this.progressionOrderFormats = data as string[];
            });

        this.rodeoService.getSecretaries()
            .then(data => {
                this.secretaries = (data as any);
            })

        setTimeout(() => {
            this.getUpdatedRegistrations();
        }, 0);

        setTimeout(() => {
            this.getUpdatedRuns();
        }, 0);

        setTimeout(() => {
            this.getUpdatedWinners();
        }, 0);

        this.loadingData = false;
    }

    unload() {
        this.events.unsubscribe("RodeoDrawn");
        for (let i = 0; i < this.subscriptions.length; i++) {
            let subscription = this.subscriptions[i];

            if (!subscription.closed)
                subscription.unsubscribe();
        }

        this.results.stopTimerLoops();
        this.scoring.stopTimerLoops();
    }

    rodeoSelected(rodeo: Rodeo) {
        this.rodeo = rodeo;

        this.setRodeoDateFields();

        this.rodeo.rodeoEvents.forEach((rodeoEvent) => {
            if (!this.rodeo.payoutConfigs || this.rodeo.payoutConfigs.filter(x => x.sources.some(y => y.rodeoEventId == rodeoEvent.id)).length == 0) {
                this.addDefaultPayoutConfigsForClass(rodeoEvent);

                rodeoEvent.sidepots.forEach((sidepot) => {
                    this.addDefaultPayoutConfigsForSidepot(rodeoEvent, sidepot);
                })
            }

            this.rodeo.payoutConfigs.forEach((payoutConfig) => {
                if (payoutConfig.divisionOffsets) {
                    payoutConfig.divisionOffsetArray = payoutConfig.divisionOffsets.split(",");
                } else {
                    payoutConfig.divisionOffsetArray = [];
                }

                if (payoutConfig.manualPayouts && payoutConfig.manualPayouts.length > 0) {
                    payoutConfig.manualPayoutsArray = payoutConfig.manualPayouts.split(',');
                }
            });
        });

        if (this.rodeo.rodeoEvents.some(x => x.eventType.startsWith('Show')))
            this.showReportsVisible = true;
        else
            this.showReportsVisible = false;

        this.farmtekReader.rodeoId = this.rodeo.id;
        this.farmtekReader.accountId = AccountHelper.instance().getAccountId();

        setTimeout(() => {
            if (!this.rodeo.id) {
                this.showSettings();
            } else if (this.viewMode === 'search') {
                this.viewMode = 'entries';
                this.viewSubMode = 'overview';
            }
        }, 0);

        this.getRodeoPendingRequests();
        this.loadRodeoOrgStaff();
    }

    addDefaultPayoutConfigsForClass(rodeoEvent) {
        let newPayoutConfig = new PayoutConfig();
        newPayoutConfig.id = Math.floor(Math.random() * -1000000000);
        newPayoutConfig.name = rodeoEvent.maxDivisions > 1 ? 'Ds' : 'Round 1';
        newPayoutConfig.maxDivisions = rodeoEvent.maxDivisions;
        newPayoutConfig.divisionOffsetArray = rodeoEvent.divisionOffsets;
        newPayoutConfig.divisionOffsets = rodeoEvent.divisionOffsets ? rodeoEvent.divisionOffsets.join(',') : rodeoEvent.divisionOffsets;
        newPayoutConfig.includePenalties = rodeoEvent.maxDivisions > 1 ? false : true;
        newPayoutConfig.places = 3;
        newPayoutConfig.payoutFormatId = this.rodeo.organization.payoutFormats ? this.rodeo.organization.payoutFormats[0].payoutFormat.id : 1;
        newPayoutConfig.rodeoId = this.rodeo.id;
        newPayoutConfig.uniqueRunsOnly = true;
        newPayoutConfig.onXRounds = 0;

        let newPayoutSource = new PayoutSource();
        newPayoutSource.amountToPot = Math.round(+rodeoEvent.entryCost * .8);
        newPayoutSource.rodeoEventId = rodeoEvent.id;
        newPayoutSource.addToCount = true;
        newPayoutSource.round = 0;
        newPayoutSource.onXRounds = 0;

        newPayoutConfig.sources = [newPayoutSource];
        this.rodeo.payoutConfigs.push(newPayoutConfig);
    }

    addDefaultPayoutConfigsForSidepot(rodeoEvent, sidepot: Sidepot) {
        let newPayoutConfig = new PayoutConfig();
        newPayoutConfig.id = Math.floor(Math.random() * -1000000000);
        newPayoutConfig.name = rodeoEvent.maxDivisions > 1 ? 'Ds' : 'Round 1';
        newPayoutConfig.maxDivisions = rodeoEvent.maxDivisions;
        newPayoutConfig.divisionOffsetArray = rodeoEvent.divisionOffsets;
        newPayoutConfig.divisionOffsets = rodeoEvent.divisionOffsets ? rodeoEvent.divisionOffsets.join(',') : rodeoEvent.divisionOffsets;
        newPayoutConfig.includePenalties = rodeoEvent.maxDivisions > 1 ? false : true;
        newPayoutConfig.places = 3;
        newPayoutConfig.payoutFormatId = this.rodeo.organization.payoutFormats ? this.rodeo.organization.payoutFormats[0].payoutFormat.id : 1;
        newPayoutConfig.rodeoId = this.rodeo.id;
        newPayoutConfig.uniqueRunsOnly = true;
        newPayoutConfig.onXRounds = 0;

        let newPayoutSource = new PayoutSource();
        newPayoutSource.amountToPot = Math.round(+rodeoEvent.priceTiers[0] * .8);
        newPayoutSource.rodeoEventId = rodeoEvent.id;
        newPayoutSource.addToCount = true;
        newPayoutSource.round = 0;
        newPayoutSource.onXRounds = 0;

        if (sidepot) {
            newPayoutConfig.name = sidepot.name;
            newPayoutSource.sidepotId = sidepot.id;
            newPayoutSource.amountToPot = Math.round(+sidepot.cost * .8);
        }

        newPayoutConfig.sources = [newPayoutSource];
        this.rodeo.payoutConfigs.push(newPayoutConfig);
    }

    async getUpdatedRuns() {
        let timeout = 1000;

        if (this.rodeo && this.rodeo.id && this.viewCtrl.isLast() && !this.popoverActive) {
            let runUpdateInfo = [];
            for (const rodeoEvent of this.rodeo.rodeoEvents) {
                for (const schedule of rodeoEvent.schedules) {
                    for (const run of schedule.runs) {
                        runUpdateInfo.push({ Id: run.id, Updated: run.updated });
                    }
                }
            }

            try {
                //console.log("calling getUpdatedRuns");
                const data = await this.runService.getUpdatedEventRuns(this.rodeo.id, runUpdateInfo) as any;
                //console.log("got getUpdatedRuns data");
                let runUpdatesHappened = false;
                let runDeletesHappened = false;
                let runAddsHappened = false;

                data.update.forEach(run => {
                    let foundRun = false;
                    //console.log("assessing update run " + run.number + ' ing round ' + run.round);
                    for (let i = 0; i < this.rodeo.rodeoEvents.length; i++) {
                        for (let x = 0; x < this.rodeo.rodeoEvents[i].schedules.length; x++) {
                            if (this.rodeo.rodeoEvents[i].schedules[x].id != run.rodeoEventScheduleId)
                                continue;

                            for (let y = 0; y < this.rodeo.rodeoEvents[i].schedules[x].runs.length; y++) {
                                if (this.rodeo.rodeoEvents[i].schedules[x].runs[y].id == run.id) {
                                    //console.log("updating run " + run.number + ' in round ' + run.round);
                                    Object.assign(this.rodeo.rodeoEvents[i].schedules[x].runs[y], run);
                                    foundRun = true;
                                    break;
                                }
                            }

                            if (foundRun)
                                break;
                        }

                        if (foundRun)
                            break;
                    }

                    runUpdatesHappened = runUpdatesHappened || foundRun;
                });

                if (runUpdatesHappened)
                    this.events.publish("RunUpdated", (data as any).update);

                data.delete.forEach(runUpdateInfo => {
                    if (this.runService.hasAdded(runUpdateInfo.id))
                        return;

                    runDeletesHappened = true;

                    for (let i = 0; i < this.rodeo.rodeoEvents.length; i++) {
                        for (let x = 0; x < this.rodeo.rodeoEvents[i].schedules.length; x++) {
                            this.rodeo.rodeoEvents[i].schedules[x].runs = this.rodeo.rodeoEvents[i].schedules[x].runs.filter(x => x.id != runUpdateInfo.id);
                        }
                    }
                });

                if (runDeletesHappened)
                    this.events.publish("RunDeleted", (data as any).delete);

                data.add.forEach(run => {
                    //console.log("assessing add run " + run.number + ' to round ' + run.round);
                    if (this.runService.hasDeleted(run.id) || this.runService.hasAdded(run.id))
                        return;

                    let foundRun = false;
                    for (let i = 0; i < this.rodeo.rodeoEvents.length; i++) {
                        for (let x = 0; x < this.rodeo.rodeoEvents[i].schedules.length; x++) {
                            if (this.rodeo.rodeoEvents[i].schedules[x].id != run.rodeoEventScheduleId)
                                continue;
                            
                            if (this.rodeo.rodeoEvents[i].schedules[x].runs.filter(x => x.id == run.id).length == 0) {
                                //console.log("adding new run " + run.number + ' to round ' + run.round);
                                foundRun = true;
                                this.rodeo.rodeoEvents[i].schedules[x].runs.push(run);
                            }

                            if (foundRun)
                                break;
                        }

                        if (foundRun)
                            break;
                    }
                    

                    runAddsHappened = runAddsHappened || foundRun;
                });

                if (runAddsHappened) {
                    this.events.publish("RunAdded", (data as any).add);
                }

                if (runAddsHappened || runUpdatesHappened || runDeletesHappened) {
                    //console.log("RunChangesHappened : " + runAddsHappened + ' ' + runUpdatesHappened + ' ' + runDeletesHappened);
                    this.events.publish("RunChangesHappened", data);
                }

            } catch (err) {
                console.error('GetUpdatedRuns', err);
            } finally {
                setTimeout(() => {
                    this.getUpdatedRuns();
                }, timeout);
            }
        } else {
            setTimeout(() => {
                this.getUpdatedRuns();
            }, timeout);
        }
    }

    async getUpdatedWinners() {
        let timeout = 1000;

        if (this.rodeo && this.rodeo.id && this.viewCtrl.isLast() && !this.popoverActive) {
            let winnerUpdateInfo = [];
            for (const rodeoEvent of this.rodeo.rodeoEvents) {
                for (const winner of rodeoEvent.winners) {
                    winnerUpdateInfo.push({ Id: winner.id, Updated: winner.updated });
                }
            }

            try {
                const data = await this.winnerService.getUpdatedEventWinners(this.rodeo.id, winnerUpdateInfo) as any;

                let winnerUpdatesHappened = false;
                let winnerDeletesHappened = false;
                let winnerAddsHappened = false;

                data.update.forEach(winner => {
                    let foundWinner = false;
                    //console.log("assessing update run " + run.number + ' ing round ' + run.round);
                    for (let i = 0; i < this.rodeo.rodeoEvents.length; i++) {
                        if (winner.rodeEventId != this.rodeo.rodeoEvents[i].id)
                            continue;

                        for (let x = 0; x < this.rodeo.rodeoEvents[i].winners.length; x++) {
                            if (this.rodeo.rodeoEvents[i].winners[x].id != winner.id)
                                continue;

                            //console.log("updating run " + run.number + ' in round ' + run.round);
                            Object.assign(this.rodeo.rodeoEvents[i].winners[x], winner);
                            foundWinner = true;
                            break;
                        }

                        if (foundWinner)
                            break;
                    }

                    winnerUpdatesHappened = winnerUpdatesHappened || foundWinner;
                });

                if (winnerUpdatesHappened)
                    this.events.publish("WinnerUpdated", (data as any).update);

                data.delete.forEach(runUpdateInfo => {
                    if (this.runService.hasAdded(runUpdateInfo.id))
                        return;

                    winnerDeletesHappened = true;

                    for (let i = 0; i < this.rodeo.rodeoEvents.length; i++) {
                        this.rodeo.rodeoEvents[i].winners = this.rodeo.rodeoEvents[i].winners.filter(x => x.id != runUpdateInfo.id);;
                    }
                });

                if (winnerDeletesHappened)
                    this.events.publish("WinnerDeleted", (data as any).delete);

                data.add.forEach(winner => {
                    //console.log("assessing add run " + run.number + ' to round ' + run.round);
                    if (this.winnerService.hasDeleted(winner.id) || this.winnerService.hasAdded(winner.id))
                        return;

                    let foundWinner = false;
                    for (let i = 0; i < this.rodeo.rodeoEvents.length; i++) {
                        if (this.rodeo.rodeoEvents[i].id != winner.rodeoEventId)
                            continue;
                        
                        if (this.rodeo.rodeoEvents[i].winners.filter(x => x.id == winner.id).length == 0) {
                            //console.log("adding new run " + run.number + ' to round ' + run.round);
                            foundWinner = true;
                            this.rodeo.rodeoEvents[i].winners.push(winner);
                        }

                        if (foundWinner)
                            break;
                    }
                    

                    winnerAddsHappened = winnerAddsHappened || foundWinner;
                });

                if (winnerAddsHappened) {
                    this.events.publish("WinnerAdded", (data as any).add);
                }

                if (winnerAddsHappened || winnerUpdatesHappened || winnerDeletesHappened) {
                    //console.log("RunChangesHappened : " + runAddsHappened + ' ' + runUpdatesHappened + ' ' + runDeletesHappened);
                    this.events.publish("WinnerChangesHappened", data);
                }

            } catch (err) {
                console.error('GetUpdatedWinners', err);
            } finally {
                setTimeout(() => {
                    this.getUpdatedWinners();
                }, timeout);
            }
        } else {
            setTimeout(() => {
                this.getUpdatedWinners();
            }, timeout);
        }
    }

    async getUpdatedRegistrations() {
        let timeout = 1000;

        if (this.rodeo && this.rodeo.id && this.viewCtrl.isLast() && !this.popoverActive) {
            let regUpdateInfo = [];

            this.rodeo.rodeoEvents.forEach((rodeoEvent) => {
                rodeoEvent.registrations.forEach((registration) => {
                    regUpdateInfo.push({ Id: registration.id, Updated: registration.updated });
                });
            });

            try {
                const data = await this.entryService.getUpdatedRegistrationsNew(this.rodeo.id, regUpdateInfo) as any;
                data.update.forEach(reg => {
                    let foundReg = false;
                    for (let i = 0; i < this.rodeo.rodeoEvents.length; i++) {
                        if (this.rodeo.rodeoEvents[i].id != reg.rodeEventId)
                            continue;

                        for (let x = 0; x < this.rodeo.rodeoEvents[i].registrations.length; x++) {
                            if (this.rodeo.rodeoEvents[i].registrations[x].id != reg.id)
                                continue;


                            Object.assign(this.rodeo.rodeoEvents[i].registrations[x], reg);
                            foundReg = true;

                            if (foundReg)
                                break;
                        }

                        if (foundReg)
                            break;
                    }
                });

                if (data.update.length > 0)
                    this.events.publish("RegistrationUpdated", (data as any).update);

                data.delete.forEach(regUpdateInfo => {
                    for (let i = 0; i < this.rodeo.rodeoEvents.length; i++) {
                        this.rodeo.rodeoEvents[i].registrations = this.rodeo.rodeoEvents[i].registrations.filter(x => x.id != regUpdateInfo.id);
                    }
                });

                if (data.delete.length > 0) {
                    this.events.publish("RegistrationDeleted", (data as any).delete);
                }

                data.add.forEach(reg => {
                    if (this.entryService.deleted.some(x => x == reg.id))
                        return;

                    let foundReg = false;
                    for (let i = 0; i < this.rodeo.rodeoEvents.length; i++) {
                        if (this.rodeo.rodeoEvents[i].id == reg.rodeoEventId) {
                            foundReg = true;

                            if (!this.rodeo.rodeoEvents[i].registrations.some(x => x.id == reg.id))
                                this.rodeo.rodeoEvents[i].registrations.push(reg);
                        }

                        if (foundReg)
                            break;
                    }
                });

                if (data.add.length > 0) {
                    this.events.publish("RegistrationAdded", (data as any).add);
                }
            } catch (err) {
                console.error('GetUpdatedRegistrations', err);
            } finally {
                setTimeout(() => {
                    this.getUpdatedRegistrations();
                }, timeout);
            }
        } else {
            setTimeout(() => {
                this.getUpdatedRegistrations();
            }, timeout);
        }
    }

    showSearch() {
        this.viewMode = 'search';
        this.rodeoList.load();
    }

    showEntries() {
        this.viewMode = 'entries';
        this.viewSubMode = 'overview';
    }

    showDraw() {
        this.viewMode = 'draw';
        this.viewSubMode = 'runs';
    }

    showScoring() {
        this.viewMode = 'scoring';
        this.scoring.startTimers();
        this.scoring.rodeo = this.rodeo;
    }

    showPhotos() {
        this.viewMode = 'photos';
        this.viewSubMode = 'upload';
        this.photos.startTimers();
    }

    showVideos() {
        this.viewMode = 'videos';
        this.videos.startTimers();
    }

    showResults() {
        this.viewMode = 'results';
        this.viewSubMode = 'payouts';
    }

    showStalls() {
        this.viewMode = 'stalls';
    }

    showSettings() {
        this.viewMode = 'settings';
        this.viewSubMode = 'general';
    }

    showReports() {
        this.viewMode = 'reports';
        this.viewSubMode = 'general';
    }

    showTransactions() {
        this.viewMode = 'transactions';
    }

    showPaymentPlans() {
        this.viewMode = 'paymentPlans';
    }

    showPayments() {
        const link = document.createElement('a');
        const token = encodeURIComponent(JSON.stringify(this.accountHelper.token));
        // console.log(token);
        link.href = 'https://nextgendemo.z19.web.core.windows.net/payments/dashboard?rodeoId=' + this.rodeo.id + '&token=' + token;
        link.target = '_blank';

        link.click();
    }

    showMembers() {
        this.viewMode = 'members';
        this.viewSubMode = 'users';
    }

    getEntryCount(rodeoEvent: RodeoEvent, schedule: RodeoEventSchedule) {
        return rodeoEvent.registrations.filter(x => x.scheduleIds[0] == schedule.id).length;
    }

    updateEntryBuddyList(entry: RodeoEventRegistration) {
        for (let i = 0; i < entry.buddies.length; i++) {
            if (entry.buddies[i].id == this.account.id)
                continue;

            if (i == 0)
                entry.buddyList = entry.buddies[i].firstName + ' ' + entry.buddies[i].lastName;
            else
                entry.buddyList = entry.buddyList + ', ' + entry.buddies[i].firstName + ' ' + entry.buddies[i].lastName;
        }
    }

    back() {
        this.nav.pop();
    }

    isSecretaryOrMore() {
        if (this.account)
            return this.account.roles.some(x => (x.organizationId == this.rodeo.organization.id && x.roleId >= 4) || x.roleId >= 6);
    }

    updatePendingRequestCount(count: number) {
        this.pendingRequestCount = count;
    }

    async getRodeoPendingRequests() {
        const searchOptions = new SearchOptions();

        searchOptions.organizations = [this.rodeo.organization];
        searchOptions.staffSearch = true;

        const rodeos: any = await this.rodeoService.searchRodeos(searchOptions);
            
        if (rodeos) {
            const rodeo = rodeos.find(x => x.id === this.rodeo.id);

            if (rodeo) {
                searchOptions.rodeos.push(rodeo);
                searchOptions.status = "Pending";
                console.log('search options', searchOptions);

                const requestResults: any = await this.requestService.searchRequests(searchOptions);

                if (requestResults) {
                    this.pendingRequestCount = requestResults.filter(x => x.status === ChangeRequestStatus.Pending).length;
                }
            }
        }
    }

    loadRodeoOrgStaff() {
        this.orgService.getAllStaffAccounts(this.rodeo.organization.id).then((data) => {
            if (data) {
                this.rodeoOrgStaff = data;
            }
        })
    }

    private setRodeoDateFields() {
        this.dateHelper.rodeoDateFields.forEach((option) => {
            if (this.rodeo[option]) {
                this.rodeo[option] = this.dateHelper.formatDateToSelectedTimeZone(this.rodeo[option], this.rodeo.rodeoTimezone);
            }
        })
    
        this.rodeo.rodeoEvents.forEach((rodeoEvent) => {
            rodeoEvent.schedules.forEach((schedule) => {
                schedule.scheduledDate = this.dateHelper.formatDateToSelectedTimeZone(schedule.scheduledDate, this.rodeo.rodeoTimezone);
            });
        });
    }
}


// WEBPACK FOOTER //
// ./src/pages/staff/rodeo/rodeo-manage.ts